import React, { useMemo } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import _, { isArray } from 'lodash';
import { useSelector } from 'react-redux';
import bs from '../../bsApp';
import { classNames } from '../../../../utils';
import { BsGenderIcon, BsLinkIcon } from '../../../../components/Icon';
import { getCubicleV2DisplayBedNo, getCubicleV2DisplayWardNo } from '../../../../utils/newBedStatusUtil';

const handleRootRotate = (bedHead) => {
  switch (bedHead) {
    case 'top':
      return '0deg';
    case 'right':
      return '90deg';
    case 'bottom':
      return '180deg';
    case 'left':
      return '270deg';

    default:
      break;
  }
};

const handleBedNoRotate = (bedHead) => {
  switch (bedHead) {
    case 'top':
      return '0deg';
    case 'right':
      return '270deg';
    case 'bottom':
      return '180deg';
    case 'left':
      return '90deg';

    default:
      break;
  }
};

const useStyles = makeStyles((theme) => ({
  root: ({ rootBg, rootBorder, bedHead }) => ({
    width: '2.188rem',
    height: '3.125rem',
    borderRadius: '0.35rem',
    background: rootBg,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    border: rootBorder,
    position: 'relative',
    transform: `rotate(${handleRootRotate(bedHead)})`,
  }),
  box1: ({ boxBorder, boxOpacity }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
    height: '0.937rem',
    width: '100%',
    borderBottom: `0.125rem solid ${boxBorder}`,
    opacity: boxOpacity,
  }),
  box2: ({ boxBorder, boxOpacity }) => ({
    width: '1.25rem',
    height: '0.625rem',
    borderRadius: '0.187rem',
    border: `0.125rem solid ${boxBorder}`,
    borderBottom: '0',
    display: 'inline-block',
    marginTop: '-0.625rem',
    opacity: boxOpacity,
    position: 'relative',
  }),
  bedNo: ({ bedHead }) => ({
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    transform: `rotate(${handleBedNoRotate(bedHead)})`,
  }),
  bedNoFont: ({ bedNoFont }) => ({
    fontFamily: 'NotoSans-Bold',
    fontSize: '1rem',
    fontWeight: 'bold',
    color: bedNoFont,
    lineHeight: '1rem',
  }),
  ward: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.625rem',
    lineHeight: '0.8rem',
  },
  linkIcon: {
    fontSize: '1.3rem',
    position: 'absolute',
    right: 0,
    bottom: 0,
    transform: 'translate(35%, 35%)',
    color: '#538fff',
  },
  genderIcon: ({ bedNoFont }) => ({
    fontSize: '0.7rem',
    color: bedNoFont,
    // transform: 'translate(3px, 0px)',
    width: '0.719rem',
    height: '0.625rem',
    position: 'absolute',
    left: '3px',
  }),
}));

const styles = (statusType, genderType, bedHead) => {
  const gender = {
    [bs.MEN]: { rootBg: '#BAD2FF', bedNoFont: '#3673e4' },
    [bs.FEMALE]: { rootBg: '#FFD5E2', bedNoFont: '#f54c9d' },
    [bs.X]: { rootBg: '#BAD2FF', bedNoFont: '#7B80FF' },
    default: { rootBg: '#000', bedNoFont: '#fff' },
  };

  const _styles = {
    [bs.AVAILABLE]: { rootBg: '#A9EDDD', bedNoFont: '#00ac84' },
    [bs.OCCUPIED]: gender[genderType] ?? gender.default,
    [bs.DISCHARGING]: gender[genderType] ?? gender.default,
    [bs.TBC]: {
      rootBg: '#fff',
      bedNoFont: '#7d86a9',
      rootBorder: '0.125rem solid #7d86a9',
      boxBorder: '#7D86A9',
      boxOpacity: '1',
    },
    [bs.UNAVAILABLE]: { rootBg: '#D0D3E0', bedNoFont: '#667098' },
    [bs.MISSING_CLOSED]: { rootBg: '#FFD983', bedNoFont: '#e49200' },
    [bs.MISSING]: { rootBg: '#FFF3BB', bedNoFont: '#dab609' },
  };

  const defaultStyles = { boxBorder: _styles[statusType] ? '#fff' : 'unset', rootBorder: 'unset', boxOpacity: '0.59' };
  return { ...defaultStyles, ..._styles[statusType], bedHead };
};

export default function StatusIcon({
  bedNo,
  typeName,
  genderType,
  ward,
  style,
  linkedBedNo,
  linkeds,
  linkedId,
  bedHead = 'top',
  editFloorPlan = false,
  newRemarks,
  allData,
  isMutipleWardCode = true,
}) {
  const classes = useStyles(style ?? styles(typeName, genderType, bedHead));
  const { isCubicleV2 } = false;

  const isLinked = useMemo(() => {
    if (linkedId) {
      return true;
    }
    return isArray(linkeds) && linkeds.length > 1;
  }, [linkeds, linkedId]);

  const showHead = useMemo(() => {
    if (typeName === bs.OCCUPIED || typeName === bs.DISCHARGING) {
      return !_.some(newRemarks, (item) => item === bs.HOME_LEAVE || item === bs.HOME_LEAVE_OVERTIME);
    } else {
      return false;
    }
  }, [typeName, newRemarks]);

  const displayBedNo = getCubicleV2DisplayBedNo({ isLinked, linkedBedNo, status: allData?.status, bedNo });
  const displayWardNo = getCubicleV2DisplayWardNo({
    isLinked,
    isMutipleWardCode,
    status: allData?.status,
    ward: allData?.ccWardCd,
  });

  return (
    <div className={classes.root}>
      <div className={classes.box1} />
      <div className={classes.box2}>{showHead && <BsGenderIcon className={classes.genderIcon} />}</div>
      {isCubicleV2 ? (
        <Box className={classes.bedNoFont} display="flex" flexDirection="column" alignItems="center">
          <div className={classes.bedNo}>{displayBedNo}</div>
          <div className={classes.ward} style={{ transform: `rotate(${handleBedNoRotate(bedHead)})` }}>
            {displayWardNo}
          </div>
        </Box>
      ) : (
        <div className={classNames(classes.bedNo, classes.bedNoFont)}>
          {isLinked ? linkedBedNo : bedNo}
          {!isLinked && <div className={classes.ward}>{ward}</div>}
        </div>
      )}
      {isLinked && editFloorPlan && <BsLinkIcon className={classes.linkIcon} />}
    </div>
  );
}
